import { useState, useRef, useMemo } from "react";
import Layout from "./common/Layout";
import Back from "./common/Back";
import { useTranslation } from "react-i18next";
import Spinner from "./common/Spinner";
import HostedField from "./common/HostedField";
import {
  useApi,
  useCompany,
  useFirstName,
  useIdentity,
  useLastName,
} from "../store/hooks";
import { isEmpty } from "../utils/utils";
import { GoToTheNextStep } from "./Step";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Toast from "./common/Toast";
const PayeeVerification = () => {
  const { t } = useTranslation();
  const { checkVop, getWorkflow } = useApi();

  const ibanRef = useRef(null);
  const firstName = useFirstName();
  const lastName = useLastName();
  const company = useCompany();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [ibanFocused, setIbanFocused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInvalidIban, setIsInvalidIban] = useState(false);

  const [formData, setFormData] = useState({
    companyName: company?.name || "",
    authorizedPerson:
      isEmpty(firstName) && isEmpty(lastName)
        ? ""
        : firstName + " " + lastName || "",
    iban: "",
  });

  const handleChange = (e) => {
    setIsInvalidIban(false);
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleHostedFieldChange = (data) => {
    setIsInvalidIban(false);
    setFormData((prevState) => ({
      ...prevState,
      iban: data.value || "",
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!isFormValid) return;
      setIsProcessing(true);
      const ibanToken = await ibanRef.current?.getValueToken();
      if (isEmpty(ibanToken)) {
        throw new Error("Cannot Parse Iban");
      }
      await checkVop(ibanToken);
      await GoToTheNextStep(navigate, dispatch, getWorkflow);
    } catch (error) {
      if (error?.response.httpStatusCode === 401) {
        GoToTheNextStep(navigate, dispatch, getWorkflow);
      } else {
        console.error("Error verifying VOP:", error);
        setIsInvalidIban(true);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isFormValid = useMemo(() => !isEmpty(formData.iban), [formData.iban]);

  return (
    <Layout>
      <div className="az-title">{t("sepaMandate.setup.title")}</div>
      <div className="az-subtitle mt-4">{t("sepaMandate.subtitle")}</div>
      <div className="mt-2">
        <form onKeyDown={handleKeyDown}>
          <div className="az-formfield">
            <input
              type="text"
              className="az-formfield__input"
              id="companyName"
              name="companyName"
              placeholder=""
              value={formData.companyName}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={true}
            />
            <label className="az-formfield__label" htmlFor="companyName">
              {t("sepaMandate.companyName.label")}
            </label>
          </div>

          <div className="az-formfield mt-3">
            <input
              type="text"
              className="az-formfield__input"
              id="authorizedPerson"
              name="authorizedPerson"
              placeholder=""
              value={formData.authorizedPerson}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={true}
            />
            <label className="az-formfield__label" htmlFor="authorizedPerson">
              {t("sepaMandate.authorizedPerson.label")}
            </label>
          </div>

          <div
            className={`az-formfield mt-3${ibanFocused ? " az-formfield--focused" : ""}${formData.iban ? " az-formfield--filled" : ""}`}
            style={{ position: "relative" }}
          >
            <HostedField
              id="iban"
              cssUrl={`${process.env.REACT_APP_FRONT_DOMAIN}/custom-field.css`}
              ref={ibanRef}
              name="iban"
              type="iban"
              placeholder=" "
              onChange={handleHostedFieldChange}
              onKeyDown={handleHostedFieldChange}
              onInput={handleHostedFieldChange}
              onFocus={() => setIbanFocused(true)}
              onBlur={() => setIbanFocused(false)}
            />
            {isProcessing && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  cursor: "not-allowed",
                  pointerEvents: "all",
                }}
              />
            )}
            <label className="az-formfield__label">
              {t("sepaMandate.iban.label")}
            </label>
          </div>

          <div
            className={`az-btn text-uppercase mt-5 ${isFormValid ? "" : "disabled"} ${isProcessing ? "disabled" : ""}`}
            onClick={handleSubmit}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
          >
            {isProcessing ? <Spinner size={24} /> : t("sepaMandate.btn.label")}
          </div>

          {isInvalidIban && (
            <Toast text={t("sepaMandate.invalidIban.error")} isError={true} />
          )}
        </form>
      </div>

      <Back message={t("back.label")} />
    </Layout>
  );
};

export default PayeeVerification;
